import { FormControl, TextField } from "@mui/material"
import { LocalizationProvider } from "@mui/x-date-pickers"
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns"
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker"
import { MobileDatePicker } from "@mui/x-date-pickers/MobileDatePicker"
import { IRootState } from "app/model"
import { getDateFnsAdapter, getDateWithMaxTime, getDateWithMinTime, isValidDate } from "app/util/date/tools/common"
import { checkTouched, isTouched as hasBeenTouched } from "form/tools/validation"
import { Field, FieldProps, FormikTouched } from "formik"
import React from "react"
import { connect } from "react-redux"
import { MAX_TABLET_WIDTH_IN_PX } from "ui/model"

interface IFieldDateProps extends IFieldDateConnectProps {
  fieldName: string // ex: "main.order_id"
  label: string
  toTouchFieldName?: string // will touch this field if changed
  disabled?: boolean
  isReadOnly?: boolean
  required?: boolean
  value: Date | null
  error?: string
  sx?: any
  minTime?: boolean
  maxTime?: boolean
  avoidDateInPast?: boolean
}

interface IFieldDateConnectProps {
  isMobile: boolean
}

const FieldDateFn = React.memo(
  ({
    fieldName,
    label,
    toTouchFieldName,
    disabled,
    required,
    isReadOnly,
    value,
    error,
    sx,
    minTime,
    maxTime,
    isMobile,
    avoidDateInPast: disabledPastDate
  }: IFieldDateProps) => {
    const DatePicker = isMobile ? MobileDatePicker : DesktopDatePicker
    const touchIfNeeded = (touched: FormikTouched<any>) => {
      checkTouched(touched, fieldName)
      if (toTouchFieldName) {
        checkTouched(touched, toTouchFieldName)
      }
    }
    const getTimedValue = (date: Date | null): Date | null => {
      if (!date) {
        return date
      }
      if (minTime) {
        return getDateWithMinTime(date)
      } else if (maxTime) {
        return getDateWithMaxTime(date)
      }
      return date
    }
    return (
      <Field name={fieldName}>
        {({ form: { touched, setFieldValue } }: FieldProps) => {
          const isTouched: boolean = hasBeenTouched(touched, fieldName)
          const isDisabled: boolean = disabled || Boolean(isReadOnly)
          const hasError = isTouched ? error !== undefined && error !== "" : false
          const onBlur = () => {
            if (!isTouched) {
              touchIfNeeded(touched)
              setFieldValue(fieldName, value)
            }
          }
          return (
            <FormControl
              id={fieldName}
              error={hasError}
              required={required}
              sx={{
                "& .MuiInputBase-root": {
                  height: "2.5rem"
                },
                "& label.MuiInputLabel-formControl": {
                  top: "-0.3rem"
                },
                "& label.MuiInputLabel-formControl.Mui-focused, & label.MuiInputLabel-formControl.MuiInputLabel-shrink":
                  {
                    top: 0
                  },
                "& .MuiFormHelperText-root": {
                  color: theme => theme.palette.error.main
                },
                "& fieldset": {
                  borderColor: theme => (hasError ? theme.palette.error.main : theme.palette.text.primary)
                },
                "& .MuiFormLabel-root.MuiInputLabel-root": {
                  color: theme => (hasError ? theme.palette.error.main : theme.palette.text.secondary)
                },
                "& input": {
                  color: theme => (hasError ? theme.palette.error.main : theme.palette.text.primary)
                },
                ...sx
              }}>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={getDateFnsAdapter()}>
                <DatePicker
                  label={required ? `${label} *` : label}
                  value={value}
                  minDate={disabledPastDate ? Date.now() : null}
                  onChange={(newValue, keyboardInputValue) => {
                    if (!isTouched) {
                      touchIfNeeded(touched)
                    }
                    if (newValue === null) {
                      setFieldValue(fieldName, null)
                    } else if (
                      (keyboardInputValue === undefined || (keyboardInputValue && keyboardInputValue.length === 10)) &&
                      isValidDate(newValue)
                    ) {
                      setFieldValue(fieldName, getTimedValue(newValue as any))
                    }
                  }}
                  readOnly={isDisabled}
                  disabled={isDisabled}
                  onOpen={() => onBlur()}
                  renderInput={params => (
                    <TextField
                      {...params}
                      id={`${fieldName}-input`}
                      helperText={hasError ? error : undefined}
                      onBlur={() => onBlur()}
                    />
                  )}
                />
              </LocalizationProvider>
            </FormControl>
          )
        }}
      </Field>
    )
  }
)

export const FieldDate = connect(
  (state: IRootState): IFieldDateConnectProps => ({
    isMobile: state.ui.container.width < MAX_TABLET_WIDTH_IN_PX
  })
)(FieldDateFn)
